import { z } from "zod";
import { patientAttentionTextsSchema } from "./attention-texts";

// TES-707 — AI-assist (TES-706): the draft the admin panel receives. The
// clinician's own text never travels here; a draft is the model's suggestion
// plus enough provenance (prompt version, model, generated-at) for the panel
// to say "Genererad hh:mm" and for a later reader to know what produced it.

export const aiAssistDraftKindSchema = z.enum([
  "form_overview",
  "meeting_note",
  "coach_reply",
  // TES-1019 — one journal heading's prefill summarised, and one box's text
  // written together ("Sammanställ"), each within that heading only.
  "journal_section_summary",
  "journal_section_compose",
]);
export type AiAssistDraftKind = z.infer<typeof aiAssistDraftKindSchema>;

export const aiAssistDraftSchema = z.object({
  id: z.string(),
  kind: aiAssistDraftKindSchema,
  promptVersion: z.string(),
  model: z.string(),
  generatedAt: z.string().datetime(),
  body: z.string(),
  // True when an earlier draft for the identical input was returned instead
  // of a new model call.
  cached: z.boolean(),
  input: z.object({
    // Form submissions the readback was built from, newest first.
    submissions: z.array(
      z.object({
        templateKey: z.string(),
        templateVersion: z.number().int(),
        submittedAt: z.string().datetime(),
      }),
    ),
    // Submissions whose template could not be resolved and were left out.
    skipped: z.array(z.string()),
  }),
});
export type AiAssistDraft = z.infer<typeof aiAssistDraftSchema>;

export const AI_ASSIST_ERRORS = {
  disabled: "ai_assist_disabled",
  noInput: "ai_assist_no_input",
  providerUnavailable: "ai_assist_provider_unavailable",
  outputRejected: "ai_assist_output_rejected",
} as const;

// TES-708 — the doctor's form overview: what CODE computed (facts, flags)
// next to what the MODEL restated (draft). The split is the whole point of
// TES-706: every number and every flag in the panel is deterministic and
// testable; the model only ever supplies `draft.body`.
export const aiAssistOverviewFlagSchema = z.object({
  rule: z.string(),
  kind: z.enum(["hard_stop", "flag"]),
  severity: z.enum(["urgent", "high", "medium", "low"]),
  escalate: z.string().optional(),
  detail: z.string().optional(),
  templateKey: z.string(),
  submittedAt: z.string().datetime(),
  // TES-948 — which submission raised it, so an acute alarm raised from the
  // same submission and rule can be shown on the same row (riskFlagRows).
  submissionId: z.string().optional(),
  // TES-948 — the answer keys the evaluator named (see flagSchema).
  fieldKeys: z.array(z.string()).optional(),
  // TES-1001 part 1 — the risk code, list version and priority, when the
  // flag comes from the versioned risk-code list (see flagSchema).
  riskCode: z.string().optional(),
  riskListVersion: z.string().optional(),
  riskPriority: z.enum(["high", "elevated", "normal"]).nullable().optional(),
  riskDestination: z.string().optional(),
  // TES-948 — the patient's own answers behind the flag, resolved on the
  // server from the submission and the template version it was answered
  // under: the question label (null when that version has no such field) and
  // the answer as the doctor reads it (option labels, free text verbatim).
  // Only answered keys appear; empty when the rule named no keys.
  answers: z
    .array(
      z.object({
        fieldKey: z.string(),
        label: z.string().nullable(),
        value: z.string(),
      }),
    )
    .optional(),
});
export type AiAssistOverviewFlag = z.infer<typeof aiAssistOverviewFlagSchema>;

export const aiAssistOverviewFactsSchema = z.object({
  // Every submission the readback covers, newest first.
  submissions: z.number().int(),
  latestSubmittedAt: z.string().datetime().nullable(),
  // Newest weekly_checkin or milestone → today. Null when there is none.
  daysSinceLastCheckin: z.number().int().nullable(),
  weight: z
    .object({
      baselineKg: z.number(),
      baselineAt: z.string().datetime(),
      baselineSource: z.enum(["intake", "f01"]),
      latestKg: z.number().nullable(),
      latestAt: z.string().datetime().nullable(),
      latestSource: z.enum(["weekly_checkin", "milestone"]).nullable(),
      // latest − baseline, one decimal. Null without a later weight.
      deltaKg: z.number().nullable(),
    })
    .nullable(),
  heightCm: z.number().nullable(),
  bmi: z
    .object({
      value: z.number(),
      source: z.enum(["f01", "intake"]),
      at: z.string().datetime(),
    })
    .nullable(),
});
export type AiAssistOverviewFacts = z.infer<typeof aiAssistOverviewFactsSchema>;

export const aiAssistFormOverviewSchema = z.object({
  // The newest stored draft for this patient, or null before the first
  // "Sammanställ". GET never generates; POST does.
  draft: aiAssistDraftSchema.nullable(),
  // True when a submission arrived after the draft was generated (its input
  // hash no longer matches) — the panel shows "Uppdatera".
  stale: z.boolean(),
  facts: aiAssistOverviewFactsSchema,
  flags: z.array(aiAssistOverviewFlagSchema),
});
export type AiAssistFormOverview = z.infer<typeof aiAssistFormOverviewSchema>;

// TES-943 — the same code-computed facts and flags, served WITHOUT the AI
// switch. TES-941:
//   "Översikten får inte vara beroende av att AI-funktionen är aktiverad"
// Nothing here comes from the model, so nothing here has a reason
// to wait for Ö-147/148; only `draft`/`stale` above stay behind the switch.
export const patientFormFactsSchema = aiAssistFormOverviewSchema
  .pick({
    facts: true,
    flags: true,
  })
  .extend({
    // TES-1001 (Ö-249) — "Information att uppmärksamma". Optional so an
    // admin built before this reads a newer server, and the other way round.
    attention: patientAttentionTextsSchema.optional(),
  });
export type PatientFormFacts = z.infer<typeof patientFormFactsSchema>;

// TES-709 — the doctor's shorthand written out. The shorthand is the source;
// the forms only feed the anamnesis paragraph. The response is split by
// code on fixed headings so the panel offers each text to exactly one field
// and never to prescription, next step or the decisions.
export const AI_ASSIST_SHORTHAND_MAX = 4000;

export const aiAssistMeetingNoteRequestSchema = z.object({
  shorthand: z.string().max(AI_ASSIST_SHORTHAND_MAX).default(""),
});
export type AiAssistMeetingNoteRequest = z.infer<
  typeof aiAssistMeetingNoteRequestSchema
>;

export const aiAssistMeetingNoteSectionsSchema = z.object({
  anamnesis: z.string().nullable(),
  assessment: z.string().nullable(),
  treatmentPlanNote: z.string().nullable(),
});
export type AiAssistMeetingNoteSections = z.infer<
  typeof aiAssistMeetingNoteSectionsSchema
>;

export const aiAssistMeetingNoteSchema = z.object({
  draft: aiAssistDraftSchema,
  sections: aiAssistMeetingNoteSectionsSchema,
});
export type AiAssistMeetingNote = z.infer<typeof aiAssistMeetingNoteSchema>;

// Which clinician field a draft text was inserted into ("Använd").
export const aiAssistUsedFieldSchema = z.enum([
  "assessment",
  "treatmentPlanNote",
  "coachReply",
]);
export type AiAssistUsedField = z.infer<typeof aiAssistUsedFieldSchema>;

export const aiAssistMarkUsedRequestSchema = z.object({
  field: aiAssistUsedFieldSchema,
});
export type AiAssistMarkUsedRequest = z.infer<
  typeof aiAssistMarkUsedRequestSchema
>;

// TES-710 — the coach's reply draft for one weekly check-in. `blocked` is
// set by code before any model call: a check-in the doctor's routing owns
// gets no draft. `reply` is the model text plus the fixed sentences code
// appended for stripped topics — what the coach edits and sends.
export const aiAssistCoachReplyBlockedSchema = z.enum([
  "hard_stop",
  "doctor_routed",
]);

export const aiAssistCoachReplySchema = z.object({
  checkin: z.object({
    id: z.string(),
    submittedAt: z.string().datetime(),
    flags: z.array(z.object({ rule: z.string() })),
  }),
  blocked: aiAssistCoachReplyBlockedSchema.nullable(),
  draft: aiAssistDraftSchema.nullable(),
  reply: z.string().nullable(),
  // Fixed sentences code appended because answers were stripped (Ö-149).
  appended: z.array(z.string()),
});
export type AiAssistCoachReply = z.infer<typeof aiAssistCoachReplySchema>;
